import { DictionaryWord } from 'app/types';

export type WordStatus = 'active' | 'learned' | 'repeat' | 'new';

export interface StatusMetaEntry {
  label: string;
  /** Light theme hex */
  color: string;
  /** Dark theme hex — same hue, lighter for contrast on dark surfaces */
  darkColor: string;
  desc: string;
}

/**
 * 4-state status system from the dictionary-table prototype.
 *
 * Mapping from existing BE booleans (`isLearned`, `isActiveSpeech`):
 *   active  := isLearned && isActiveSpeech
 *   learned := isLearned && !isActiveSpeech   (legacy "needs repeat" — kept here
 *              because BE doesn't yet distinguish "fully learned" from
 *              "time to review". Once SRS lands and BE flips overdue words to
 *              a distinct REVIEW status, `statusOf` will start returning
 *              'repeat' for them.)
 *   repeat  := never yet (placeholder for SRS-driven "time to refresh")
 *   new     := !isLearned (not in user vocabulary yet)
 *
 * Кольори узгоджені з design exploration (dictionary-table/data.js).
 */
export const STATUS_META: Record<WordStatus, StatusMetaEntry> = {
  active: {
    label: 'Активне',
    color: '#27CCBC',
    darkColor: '#3FE0CE',
    desc: 'вивчено й вживаєш у мовленні',
  },
  learned: {
    label: 'Вивчено',
    color: '#5FA56F',
    darkColor: '#74C98A',
    desc: 'вивчено, ще не активне',
  },
  repeat: {
    label: 'Повторити',
    color: '#777BFF',
    darkColor: '#9DA0FF',
    desc: 'час освіжити',
  },
  new: {
    label: 'Нове',
    color: '#3B2C93',
    darkColor: '#B9AEFF',
    desc: 'ще не у твоєму словнику',
  },
};

export const STATUS_ORDER: WordStatus[] = ['active', 'learned', 'repeat', 'new'];

/**
 * Map a word to its UI status. Prefers the BE-supplied `status` field
 * (V8+); falls back to deriving from the legacy booleans for payloads
 * predating the migration. Once every endpoint includes `status`, the
 * fallback can be deleted and the input shape tightened.
 */
export const statusOf = (
  word: Pick<DictionaryWord, 'isLearned' | 'isActiveSpeech' | 'status'>,
): WordStatus => {
  switch (word.status) {
    case 'ACTIVE':
      return 'active';
    case 'LEARNED':
      return 'learned';
    case 'REPEAT':
      return 'repeat';
    case 'NEW':
      return 'new';
  }
  if (word.isActiveSpeech) return 'active';
  if (word.isLearned) return 'learned';
  return 'new';
};

export const inVocab = (status: WordStatus) => status !== 'new';

/**
 * Theme-aware hex picker. Pass `palette.mode` from the caller.
 * Use directly when you need a CSS string (inline border, box-shadow, rgba).
 */
export const pickStatusColor = (
  status: WordStatus,
  mode: 'light' | 'dark',
): string => {
  const m = STATUS_META[status];
  return mode === 'dark' ? m.darkColor : m.color;
};
